import { Button, Grid, MenuItem } from '@mui/material'
import { useField } from 'formik'
import React, { useMemo, useState } from 'react'
import { getLocationType } from '.'
import { StyledDescription } from '../../../../components/form/styled'
import TransformDisplayToEdition from '../../../../components/form/TransformDisplayToEdition'
import { useOptionalAdgroupContext } from '../../../../utils/hooks/adGroup/useAdgroupContext'
import { useAppTranslations } from '../../../../utils/hooks/useAppTranslations'
import { useCountries } from '../../../../utils/hooks/useCountries'
import mapEnumForSelect from '../../../../utils/mapEnumForSelect'
import { AdgroupSettingsQuery } from '../../../../__generated__/graphql'
import ToggleDisplay from '../../../../components/layout/collapsible/ShowMoreButtonToggle'
import CSVAddressesUploadForm from './CSVAddressesUploadForm'
import { FormDialog } from '../../../../components/layout/FormDialog'
import { useCanEditAdGroup } from '../../../../utils/hooks/adGroup/useCanEditAdGroup'
import { useUserPermissions } from '../../../company/userPermissions'

export interface GeoTargetingFieldsProps {
  adgroupTargeting: AdgroupSettingsQuery['adGroup']['targeting']
  countryCode?: string
  readOnly?: boolean
  simulationMode?: boolean
}

export enum LocationTypesEnum {
  Cities = 'cities',
  Departements = 'departements',
  Regions = 'regions',
  Countries = 'countries',
}

const locationTypeTranslationKey = (locationType: LocationTypesEnum): string => `adgroup.geoTargeting.${locationType}`

const GeoTargetingFields: React.FC<GeoTargetingFieldsProps> = ({
  adgroupTargeting,
  countryCode,
  readOnly = false,
  simulationMode = false,
}): JSX.Element => {
  const canEditAdGroup = useCanEditAdGroup()
  const editable = simulationMode || (!readOnly && canEditAdGroup)
  const { superAdmin } = useUserPermissions()
  const t = useAppTranslations()
  const { data: countryData } = useCountries()
  const [, targetingTypeField] = useField('targeting.geoExclusive')
  const [, typeMeta] = useField('targeting.type')
  const adgroupFromContext = useOptionalAdgroupContext()
  const adgroup = adgroupFromContext ?? {
    authorizedCountries: [],
    countryBlacklistRedirectUrl: null,
  }
  const { countries, geoExclusive } = adgroupTargeting?.geoTargeting ?? {}
  const [ruleExceptionIsActive, setRuleExceptionIsActive] = useState(false)
  const [uploadAddressesModal, setUploadAddressesModal] = useState(false)

  const hasInitialExcludedCountries =
    geoExclusive === true &&
    getLocationType(adgroupTargeting?.geoTargeting) === LocationTypesEnum.Countries &&
    (countries?.length ?? 0) > 0

  const geographicRuleExclusionContainerStyle = {
    marginLeft: '35px',
    width: '70%',
  }
  const geoExclusiveOptions = [
    { value: false, label: 'commons.include' },
    { value: true, label: 'commons.exclude' },
  ]

  const locationTypeOptions = useMemo(
    () =>
      mapEnumForSelect(LocationTypesEnum)
        .filter(
          (locationType) =>
            locationType.value !== LocationTypesEnum.Countries ||
            targetingTypeField?.value === true ||
            hasInitialExcludedCountries ||
            superAdmin // Allow "Countries" if geoExclusive is true in form, if initial targeting excludes countries, or user is superAdmin
        )
        .map((type) => ({ value: type.value, label: t(locationTypeTranslationKey(type.value)) })),
    [targetingTypeField.value, hasInitialExcludedCountries, superAdmin, t]
  )

  const countriesOptions = useMemo(
    () =>
      countryData?.getCountries.map((country) => (
        <MenuItem value={country.code} key={country.code}>
          {country.label}
        </MenuItem>
      )),
    [countryData]
  )

  return (
    <Grid container>
      <Grid size={10}>
        <TransformDisplayToEdition
          inputType={'radio'}
          label="adgroup.geoTargeting.rule"
          options={geoExclusiveOptions}
          name="targeting.geoExclusive"
          text={geoExclusive ? t('commons.exclude') : t('commons.include')}
          disableHoverEdition
          editable={editable}
        />
      </Grid>
      <Grid size={10}>
        <TransformDisplayToEdition
          inputType="select"
          withNone={false}
          name="targeting.type"
          label="adgroup.geoTargeting.locationType"
          text={t(locationTypeTranslationKey(getLocationType(adgroupTargeting?.geoTargeting)))}
          tooltipText="adgroup.geoTargeting.locationTypeHelper"
          disableHoverEdition
          variant="outlined"
          editable={editable}
          options={locationTypeOptions}
        />
      </Grid>
      <Grid size={10}>
        <TransformDisplayToEdition
          label="adgroup.geoTargeting.location"
          name={'targeting.' + typeMeta.value}
          text=""
          inputType={'autoCompleteGoogleMap'}
          locationType={typeMeta.value}
          countryCode={countryCode}
          disableHoverEdition
          editable={editable}
        />
      </Grid>
      {!simulationMode && (
        <Grid size={10}>
          <Button
            onClick={() => setUploadAddressesModal(true)}
            variant="text"
            color="secondary"
            sx={{ mt: 3 }}
            disabled={!editable}
          >
            {t('adgroup.geoTargeting.importCSV')}
          </Button>
        </Grid>
      )}
      {!simulationMode && uploadAddressesModal && (
        <FormDialog
          open={uploadAddressesModal}
          setOpen={() => setUploadAddressesModal(false)}
          title={t('adgroup.geoTargeting.importCSV')}
          maxWidth="sm"
        >
          <CSVAddressesUploadForm setOpen={() => setUploadAddressesModal(false)} />
        </FormDialog>
      )}
      {!simulationMode && (
        <ToggleDisplay
          handleClick={() => setRuleExceptionIsActive(!ruleExceptionIsActive)}
          isDisplayed={ruleExceptionIsActive}
        >
          {t('adgroup.rulesExceptionsLabel')}
        </ToggleDisplay>
      )}
      {!simulationMode && ruleExceptionIsActive && (
        <Grid size={12}>
          <div style={geographicRuleExclusionContainerStyle}>
            <StyledDescription>{t(`adgroup.rulesExceptions`)}</StyledDescription>
            <Grid container>
              <Grid size={8}>
                <TransformDisplayToEdition
                  inputType={'select'}
                  label="adgroup.geoTargeting.acceptedCountries"
                  data-testid="acceptedCountries"
                  name={'authorizedCountries'}
                  text={adgroup.authorizedCountries.map((country) => country.label).join('\n')}
                  multiple={true}
                  disableHoverEdition
                  variant="outlined"
                  editable={editable}
                >
                  {countriesOptions}
                </TransformDisplayToEdition>
              </Grid>
              <Grid size={8}>
                <TransformDisplayToEdition
                  inputType={'input'}
                  type={'text'}
                  data-testid="redirectUrl"
                  name={'countryBlacklistRedirectUrl'}
                  label={'adgroup.redirectUrl'}
                  text={adgroup.countryBlacklistRedirectUrl}
                  multiline={true}
                  disableHoverEdition
                  variant="outlined"
                  editable={editable}
                />
              </Grid>
            </Grid>
          </div>
        </Grid>
      )}
    </Grid>
  )
}
export default GeoTargetingFields
